import type { RouteLocationNormalized, Router } from 'vue-router';

/**
 * Recovery for a tab left open across a deploy (MOMENTUM-ADMIN-BT / BP / BY).
 *
 * Every build renames the hashed files under /assets. A tab still running the previous build asks
 * for the old names on its next lazy route or component, and the host no longer has them: a 404
 * (or, before `/assets/*` was excluded from the SPA fallback, index.html served as the script —
 * which Safari reports as a MIME type error). The running app cannot recover from that by itself;
 * the only fix is to load the current build, so this reloads the page once.
 */

/** Browser, Vite and Vue Router messages for a chunk or its stylesheet failing to load. */
const CHUNK_LOAD_ERROR_PATTERNS: RegExp[] = [
    /Failed to fetch dynamically imported module/i, // Chrome, Edge
    /Importing a module script failed/i, // Safari: the import() request failed outright
    /is not a valid JavaScript MIME type/i, // Safari: the host answered the chunk request with HTML
    /error loading dynamically imported module/i, // Firefox
    /Unable to preload CSS/i, // Vite's preload helper, for a lazy chunk's stylesheet
    // Vue Router, when a lazy route's import resolved to nothing: what Vite's preload helper hands
    // back once a `vite:preloadError` has been default-prevented (MOMENTUM-ADMIN-BY).
    /Couldn't resolve component/i
];

const RELOAD_STAMP_KEY = 'nymble-admin:chunk-reload-at';

/** At most one reload per tab per minute: a second failure right after reloading is not a stale build. */
export const RELOAD_COOLDOWN_MS = 60_000;

/** True for the errors a browser raises when a lazy chunk (or its CSS) cannot be fetched. */
export function isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export interface ChunkReloadDeps {
    /** Where the last-reload stamp lives; sessionStorage in the browser, so it is per tab. */
    storage: Pick<Storage, 'getItem' | 'setItem'> | null;
    now: () => number;
    /** Reloads the page; with `href`, loads that route fresh instead. */
    reload: (href?: string) => void;
}

/**
 * Reloads the page onto the current build, unless this tab already did so within the cooldown or
 * cannot record that it is doing so (no storage means no loop protection, so no reload).
 * Returns whether a reload was started, so the caller can suppress the error it was handling.
 */
export function reloadForStaleChunk(deps: ChunkReloadDeps, href?: string): boolean {
    if (!deps.storage) return false;
    const now = deps.now();
    try {
        const last = Number(deps.storage.getItem(RELOAD_STAMP_KEY) ?? 0) || 0;
        if (last && now - last < RELOAD_COOLDOWN_MS) return false;
        deps.storage.setItem(RELOAD_STAMP_KEY, String(now));
    } catch {
        return false;
    }
    deps.reload(href);
    return true;
}

/** The slice of `window` the recovery uses, so it can be installed on a fake in tests. */
export interface ChunkReloadWindow {
    addEventListener(type: 'vite:preloadError', listener: (event: Event) => void): void;
    location: Pick<Location, 'pathname' | 'search' | 'hash'> & { reload(): void };
    history: { pushState(data: unknown, unused: string, url: string): void };
    sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
    setTimeout(handler: () => void, timeout: number): unknown;
}

/** Reading `sessionStorage` itself can throw (blocked storage); treat that as no storage. */
function sessionStorageOf(win: ChunkReloadWindow): ChunkReloadDeps['storage'] {
    try {
        return win.sessionStorage ?? null;
    } catch {
        return null;
    }
}

/**
 * Loads `href` (or the current page) from the host. Always a reload, never `location.assign`: a
 * reload revalidates the document, where a plain navigation may be answered from the HTTP cache
 * with the old index.html, which points at the very chunks that are gone.
 *
 * Deferred to the next task so the router has finished failing the navigation first: on back /
 * forward it then rewinds history with `history.go()`, and that traversal cancels a reload
 * started before it.
 */
function reloadOnto(win: ChunkReloadWindow, href?: string): void {
    win.setTimeout(() => {
        const { pathname, search, hash } = win.location;
        if (href && href !== pathname + search + hash) win.history.pushState(null, '', href);
        win.location.reload();
    }, 0);
}

export interface ChunkReloadRecovery {
    /**
     * True for a chunk-load error raised after this page started reloading onto the current build.
     * The navigation the stale chunk broke still fails while the reload is under way, and Sentry's
     * router hook reports that failure; it is already handled, so it is not worth an issue.
     */
    isRecoveringFrom(error: unknown): boolean;
}

/**
 * Wires the recovery into the two places a stale chunk surfaces:
 * - `vite:preloadError`, which Vite's preload helper dispatches for every lazy import it wraps —
 *   lazy routes included — when the chunk or its CSS fails. Default-prevented, the helper resolves
 *   the import to nothing instead of throwing it on;
 * - the router's error hook, for a lazy route whose `import()` failed without passing through that
 *   helper (the dev server), or whose failure reached the router anyway (inside the cooldown).
 * Either way a failure during a navigation reloads onto the route the user asked for, not the page
 * they were leaving.
 */
export function installChunkReloadRecovery(
    router: Pick<Router, 'beforeEach' | 'afterEach' | 'onError'>,
    win: ChunkReloadWindow = window
): ChunkReloadRecovery {
    let reloading = false;
    const deps: ChunkReloadDeps = {
        storage: sessionStorageOf(win),
        now: () => Date.now(),
        reload: (href) => {
            reloading = true;
            reloadOnto(win, href);
        }
    };

    // The navigation in progress, if any. Lazy route components are requested after the global
    // beforeEach guards, so a chunk that fails while this is set belongs to it.
    let pending: RouteLocationNormalized | null = null;
    router.beforeEach((to) => {
        pending = to;
    });
    router.afterEach((to) => {
        if (to === pending) pending = null;
    });

    win.addEventListener('vite:preloadError', (event) => {
        if (reloadForStaleChunk(deps, pending?.fullPath)) event.preventDefault();
    });

    router.onError((error, to) => {
        if (to === pending) pending = null;
        if (isChunkLoadError(error)) reloadForStaleChunk(deps, to.fullPath);
    });

    return { isRecoveringFrom: (error) => reloading && isChunkLoadError(error) };
}
